import axios from "../utils/axios";

// ==========================================
// Bulk product operations (Backend Sprint 14.1)
// Each endpoint returns `{ data: { updated: int[], failed: [{ id, reason, message }] } }`.
// ==========================================

export const bulkUpdateProductsStatus = async (data) => {
    const response = await axios.post("/admin/products/bulk-status", data);
    return response.data;
};

export const bulkAdjustProductsPrice = async (data) => {
    const response = await axios.post("/admin/products/bulk-price-adjust", data);
    return response.data;
};

export const bulkAssignProductsCategory = async (data) => {
    const response = await axios.post("/admin/products/bulk-assign-category", data);
    return response.data;
};

export const bulkAssignProductsTag = async (data) => {
    const response = await axios.post("/admin/products/bulk-assign-tag", data);
    return response.data;
};

/**
 * Get all products with pagination and filters
 */
export const getProducts = async (params = {}) => {
    try {
        const { data } = await axios.get('/admin/products', { params });
        return data;
    } catch (error) {
        console.error("getProducts error:", error);
        throw error;
    }
};

/**
 * Get a single product by ID
 */
export const getProductById = async (id) => {
    try {
        const { data } = await axios.get(`/admin/products/${id}`);
        return data;
    } catch (error) {
        console.error("getProductById error:", error);
        throw error;
    }
};

/**
 * Create a new product
 */
export const addProduct = async (req) => {
    try {
        const { data } = await axios.post('/admin/products', req);
        return data;
    } catch (error) {
        console.error("addProduct error:", error);
        throw error;
    }
};

/**
 * Update an existing product
 * Note: We use POST with _method=PUT for file upload compatibility
 */
export const updateProduct = async (id, req) => {
    try {
        const { data } = await axios.post(`/admin/products/${id}`, req);
        return data;
    } catch (error) {
        console.error("updateProduct error:", error);
        throw error;
    }
};

/**
 * Delete a product (Soft delete)
 */
export const deleteProduct = async (id) => {
    try {
        const { data } = await axios.delete(`/admin/products/${id}`);
        return data;
    } catch (error) {
        console.error("deleteProduct error:", error);
        throw error;
    }
};

/**
 * Sync option groups to a product
 */
export const syncProductOptions = async (id, req) => {
    try {
        const { data } = await axios.put(`/admin/products/${id}/option-groups`, req);
        return data;
    } catch (error) {
        console.error("syncProductOptions error:", error);
        throw error;
    }
};
/**
 * Get all variants for a product
 */
export const getProductVariants = async (productId) => {
    try {
        const { data } = await axios.get(`/admin/products/${productId}/variants`);
        return data;
    } catch (error) {
        console.error("getProductVariants error:", error);
        throw error;
    }
};

/**
 * Generate variants for a product based on option groups
 */
export const generateProductVariants = async (productId) => {
    try {
        const { data } = await axios.post(`/admin/products/${productId}/variants/generate`);
        return data;
    } catch (error) {
        console.error("generateProductVariants error:", error);
        throw error;
    }
};

// ==========================================
// Unified Product API (with variants)
// ==========================================

/**
 * Create a new product with variants in a single request
 */
export const createProductWithVariants = async (req) => {
    try {
        const { data } = await axios.post('/admin/products/with-variants', req);
        return data;
    } catch (error) {
        console.error("createProductWithVariants error:", error);
        throw error;
    }
};

/**
 * Update a product with variants in a single request
 */
export const updateProductWithVariants = async (id, req) => {
    try {
        // For file uploads with FormData, use POST with _method=PUT
        if (req instanceof FormData) {
            req.append('_method', 'PUT');
            const { data } = await axios.post(`/admin/products/${id}/with-variants`, req, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        }
        // For JSON data, use PUT
        const { data } = await axios.put(`/admin/products/${id}/with-variants`, req);
        return data;
    } catch (error) {
        console.error("updateProductWithVariants error:", error);
        throw error;
    }
};

/**
 * Update a specific variant
 */
export const updateProductVariant = async (productId, variantId, req) => {
    try {
        const { data } = await axios.put(`/admin/products/${productId}/variants/${variantId}`, req);
        return data;
    } catch (error) {
        console.error("updateProductVariant error:", error);
        throw error;
    }
};

/**
 * Delete a specific variant
 */
export const deleteProductVariant = async (productId, variantId) => {
    try {
        const { data } = await axios.delete(`/admin/products/${productId}/variants/${variantId}`);
        return data;
    } catch (error) {
        console.error("deleteProductVariant error:", error);
        throw error;
    }
};
